import { Component, ElementRef, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { NotificationType, Reservation } from '@app/shared/interfaces';
import {
  NonDirectSecurityDepositClaim,
  ReservationService,
} from '@app/shared/services/reservation/reservation.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { PickerResponse } from 'filestack-js';

@Component({
  selector: 'app-dialog-claim-non-direct-security-deposit',
  templateUrl: './dialog-claim-non-direct-security-deposit.component.html',
})
export class DialogClaimNonDirectSecurityDepositComponent implements OnInit {
  daysUntilRelease: number;
  primaryAction = {
    label: 'Submit charge',
    onClick: () => this.charge(),
    loading: false,
  };

  secondaryAction = {
    label: 'Back',
    onClick: () => this.close(),
  };

  form = new FormGroup({
    amount: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1),
      Validators.max(this.reservation.security_deposit.amount.amount),
    ]),
    type: new FormControl<string | null>(null, [Validators.required]),
    description: new FormControl<string | null>(null, [Validators.required]),
    userConfirmation: new FormControl<boolean | null>(null, [Validators.requiredTrue]),
    images: new FormControl<string[]>([], [Validators.required]),
  });

  constructor(
    public dialogRef: MatDialogRef<DialogClaimNonDirectSecurityDepositComponent>,
    @Inject(MAT_DIALOG_DATA) public reservation: Reservation,
    private elementRef: ElementRef,
    private reservationService: ReservationService,
    private toastNotificationsService: ToastNotificationsService
  ) {}

  ngOnInit(): void {
    this.daysUntilRelease = this.calculateDaysUntilRelease();
  }

  close(): void {
    this.dialogRef.close(false);
  }

  imageUploadSuccess(event: PickerResponse) {
    const newUrls = event.filesUploaded?.map(
      (file) => `https://cdn.filestackcontent.com/quality=value:30/compress/${file.handle}`
    );

    this.form.controls.images.setValue(this.form.controls.images.value.concat(newUrls));
  }

  imageRemoved(index: number) {
    this.form.controls.images.setValue(this.form.controls.images.value.filter((_, i) => i !== index));
  }

  private charge(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      const error = this.elementRef.nativeElement.querySelector('mat-error');
      error?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.primaryAction.loading = true;
    this.reservationService
      .claimNonDirectSecurityDeposit(this.reservation.uuid, this.form.value as NonDirectSecurityDepositClaim)
      .subscribe(
        () => {
          this.toastNotificationsService.open(
            'Your charge has been submitted. You should receive the funds within 2 days.',
            'Dismiss',
            NotificationType.Success,
            10000
          );
          this.primaryAction.loading = false;
          this.dialogRef.close(true);
        },
        () => {
          this.primaryAction.loading = false;
          this.toastNotificationsService.error('We were unable to charge the security deposit');
        }
      );
  }

  private calculateDaysUntilRelease(): number {
    const releaseDate = new Date(this.reservation.security_deposit.release_at);
    const today = new Date();
    const timeDiff = releaseDate.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  }
}
